import { useMemo } from 'react';

interface MarkdownRendererProps {
  content: string;
}

/**
 * Renderizador de markdown leve para respostas do assistente IA.
 * Suporta: negrito, itálico, listas, tabelas, código inline e blocos de código.
 */
export function MarkdownRenderer({ content }: MarkdownRendererProps) {
  const html = useMemo(() => renderMarkdown(content), [content]);
  return (
    <div
      className="prose prose-sm max-w-none prose-table:border-collapse prose-td:border prose-td:border-gray-300 prose-td:px-3 prose-td:py-1 prose-th:border prose-th:border-gray-300 prose-th:px-3 prose-th:py-1 prose-th:bg-gray-100 prose-th:font-semibold"
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

function renderMarkdown(text: string): string {
  let html = escapeHtml(text);

  // Code blocks (```)
  html = html.replace(/```(\w*)\n([\s\S]*?)```/g, (_m, _lang, code) =>
    `<pre class="bg-gray-100 rounded p-3 overflow-x-auto text-sm"><code>${code.trim()}</code></pre>`
  );

  // Inline code
  html = html.replace(/`([^`]+)`/g, '<code class="bg-gray-100 px-1 rounded text-sm">$1</code>');

  // Bold
  html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');

  // Italic
  html = html.replace(/\*(.+?)\*/g, '<em>$1</em>');

  // Tables
  html = renderTables(html);

  // Lists (unordered)
  html = html.replace(/^- (.+)$/gm, '<li>$1</li>');
  html = html.replace(/(<li>.*<\/li>\n?)+/g, (match) => `<ul class="list-disc pl-5 my-2">${match}</ul>`);

  // Lists (ordered)
  html = html.replace(/^\d+\. (.+)$/gm, '<li>$1</li>');
  html = html.replace(/(<li>.*<\/li>\n?)+/g, (match) => {
    if (!match.includes('<ul')) return `<ol class="list-decimal pl-5 my-2">${match}</ol>`;
    return match;
  });

  // Headers
  html = html.replace(/^### (.+)$/gm, '<h3 class="font-semibold text-base mt-3 mb-1">$1</h3>');
  html = html.replace(/^## (.+)$/gm, '<h2 class="font-semibold text-lg mt-3 mb-1">$1</h2>');

  // Paragraphs (double newlines)
  html = html.replace(/\n\n/g, '</p><p class="my-2">');
  html = `<p class="my-2">${html}</p>`;

  // Single newlines to <br>
  html = html.replace(/\n/g, '<br>');

  // Clean up empty paragraphs
  html = html.replace(/<p class="my-2"><\/p>/g, '');

  return html;
}

function renderTables(html: string): string {
  const lines = html.split('\n');
  const result: string[] = [];
  let inTable = false;
  let tableRows: string[] = [];

  for (const line of lines) {
    if (line.trim().startsWith('|') && line.trim().endsWith('|')) {
      if (!inTable) {
        inTable = true;
        tableRows = [];
      }
      // Skip separator rows (|---|---|)
      if (/^\|[\s-:|]+\|$/.test(line.trim())) continue;
      tableRows.push(line.trim());
    } else {
      if (inTable) {
        result.push(buildTable(tableRows));
        inTable = false;
        tableRows = [];
      }
      result.push(line);
    }
  }
  if (inTable) result.push(buildTable(tableRows));

  return result.join('\n');
}

function buildTable(rows: string[]): string {
  if (rows.length === 0) return '';
  const headerCells = rows[0].split('|').filter(Boolean).map((c) => c.trim());
  let table = '<div class="overflow-x-auto my-3"><table class="min-w-full border-collapse border border-gray-300 text-sm"><thead><tr>';
  for (const cell of headerCells) {
    table += `<th class="border border-gray-300 px-3 py-1 bg-gray-100 font-semibold text-left">${cell}</th>`;
  }
  table += '</tr></thead><tbody>';
  for (let i = 1; i < rows.length; i++) {
    const cells = rows[i].split('|').filter(Boolean).map((c) => c.trim());
    table += '<tr>';
    for (const cell of cells) {
      table += `<td class="border border-gray-300 px-3 py-1">${cell}</td>`;
    }
    table += '</tr>';
  }
  table += '</tbody></table></div>';
  return table;
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}
